import {DomainNameEnum} from '../../../../provider/domain-config/domain-config-type';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {
    ExportColumnCatalogItemType,
    ExportColumnFormatType,
    ExportConstructorExportPayloadType,
    ExportTemplateColumnType,
    ExportTemplateType,
    SystemExportTemplateCodeEnum,
} from '../../../../service/reviews/export-constructor/export-constructor-type';
import {ExportReportModeEnum, ExportReportOptionsType} from '../../../../service/reviews/reviews-report';
import {getEnumValue} from '../../../../util/enum';
import {EMAIL_REGEXP} from '../../../../util/regexp';
import {normalizeEmails, splitCommaSeparatedString} from '../../../../util/string';

import {
    AddressFormatType,
    AuthorFormatType,
    ExportConstructorEditorFormEnum,
    ExportConstructorEditorFormType,
    ExportConstructorListFormType,
} from './reviews-export-constructor-types';

export const exportConstructorListFormId = 'reviews-export-constructor-list-form';
export const exportConstructorEditorFormId = 'reviews-export-constructor-editor-form';
export const commentsLayoutStorageKey = 'reviews_export_comments_layout';

const systemTemplateOrder: Array<SystemExportTemplateCodeEnum> = [
    SystemExportTemplateCodeEnum.Base,
    SystemExportTemplateCodeEnum.Base1,
    SystemExportTemplateCodeEnum.Base2,
    SystemExportTemplateCodeEnum.Base3,
    SystemExportTemplateCodeEnum.Base4,
];

const systemTemplateDescriptionKeyMap: Record<SystemExportTemplateCodeEnum, LangKeyType> = {
    [SystemExportTemplateCodeEnum.Base]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__FULL_UNLOADING_OF_REVIEWS__TEXT',
    [SystemExportTemplateCodeEnum.Base1]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__REVIEW_STATISTICS_BY_BRAND_AND_CITY__TEXT',
    [SystemExportTemplateCodeEnum.Base2]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RESPONSE_STATISTICS_BY_BRAND_AND_CITY__TEXT',
    [SystemExportTemplateCodeEnum.Base3]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_BY_SOURCE__TEXT',
    [SystemExportTemplateCodeEnum.Base4]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_GOOGLE_YANDEX_GIS__TEXT',
};

const systemTemplatePlaceholderKeyMap: Record<SystemExportTemplateCodeEnum, LangKeyType> = {
    [SystemExportTemplateCodeEnum.Base]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__FULL_UNLOADING_OF_REVIEWS__PLACEHOLDER',
    [SystemExportTemplateCodeEnum.Base1]:
        'REVIEWS__DOWNLOAD_REPORT_POPUP__REVIEW_STATISTICS_BY_BRAND_AND_CITY__PLACEHOLDER',
    [SystemExportTemplateCodeEnum.Base2]:
        'REVIEWS__DOWNLOAD_REPORT_POPUP__RESPONSE_STATISTICS_BY_BRAND_AND_CITY__PLACEHOLDER',
    [SystemExportTemplateCodeEnum.Base3]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_BY_SOURCE__PLACEHOLDER',
    [SystemExportTemplateCodeEnum.Base4]:
        'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_GOOGLE_YANDEX_GIS__PLACEHOLDER',
};

const systemTemplateHeaderKeyMap: Record<SystemExportTemplateCodeEnum, LangKeyType> = {
    [SystemExportTemplateCodeEnum.Base]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__FULL_UNLOADING_OF_REVIEWS__HEADER__NEW',
    [SystemExportTemplateCodeEnum.Base1]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__REVIEW_STATISTICS_BY_BRAND_AND_CITY__HEADER',
    [SystemExportTemplateCodeEnum.Base2]:
        'REVIEWS__DOWNLOAD_REPORT_POPUP__RESPONSE_STATISTICS_BY_BRAND_AND_CITY__HEADER',
    [SystemExportTemplateCodeEnum.Base3]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_BY_SOURCE__HEADER',
    [SystemExportTemplateCodeEnum.Base4]: 'REVIEWS__DOWNLOAD_REPORT_POPUP__RATING_STATISTICS_GOOGLE_YANDEX_GIS__HEADER',
};

export const legacyFullExportOldTemplateCode = 'base5';

// Legacy-only: полная выгрузка отзывов старой версии (mode=base, version=1).
export function isLegacyFullExportOldTemplate(template: ExportTemplateType): boolean {
    return template.code === legacyFullExportOldTemplateCode;
}

// Возвращает код системного шаблона (base, base1, …) или null для пользовательского.
export function getSystemTemplateCode(template: ExportTemplateType): SystemExportTemplateCodeEnum | null {
    if (!template.code) {
        return null;
    }

    return getEnumValue<SystemExportTemplateCodeEnum>(SystemExportTemplateCodeEnum, template.code);
}

// Ключ локализации для описания системного шаблона в списке выгрузок.
export function getSystemTemplateDescriptionKey(template: ExportTemplateType): LangKeyType | null {
    if (isLegacyFullExportOldTemplate(template)) {
        return systemTemplateDescriptionKeyMap[SystemExportTemplateCodeEnum.Base];
    }

    const code = getSystemTemplateCode(template);

    return code ? systemTemplateDescriptionKeyMap[code] : null;
}

// Ключ локализации для placeholder поля названия отчёта.
export function getSystemTemplatePlaceholderKey(template: ExportTemplateType): LangKeyType | null {
    if (isLegacyFullExportOldTemplate(template)) {
        return systemTemplatePlaceholderKeyMap[SystemExportTemplateCodeEnum.Base];
    }

    const code = getSystemTemplateCode(template);

    return code ? systemTemplatePlaceholderKeyMap[code] : null;
}

// Ключ локализации для заголовка системного шаблона в списке.
export function getSystemTemplateHeaderKey(template: ExportTemplateType): LangKeyType | null {
    if (isLegacyFullExportOldTemplate(template)) {
        return 'REVIEWS__DOWNLOAD_REPORT_POPUP__FULL_UNLOADING_OF_REVIEWS__HEADER__OLD';
    }

    const code = getSystemTemplateCode(template);

    return code ? systemTemplateHeaderKeyMap[code] : null;
}

// Сортирует шаблоны: сначала системные в фиксированном порядке, затем пользовательские по дате обновления.
function getSystemTemplateSortOrder(template: ExportTemplateType): number {
    if (isLegacyFullExportOldTemplate(template)) {
        return 1;
    }

    const code = getSystemTemplateCode(template);
    const orderIndex = systemTemplateOrder.indexOf(code ?? SystemExportTemplateCodeEnum.Base);

    return orderIndex + (orderIndex > 0 ? 1 : 0);
}

export function sortExportTemplates(
    templates: Array<ExportTemplateType>,
    domainName: string
): Array<ExportTemplateType> {
    const filteredTemplates = templates.filter((template) => {
        const code = getSystemTemplateCode(template);

        return !(code === SystemExportTemplateCodeEnum.Base4 && domainName === DomainNameEnum.brandWizard);
    });

    const systemTemplates = filteredTemplates
        .filter((template) => template.isSystem)
        .sort((left, right) => getSystemTemplateSortOrder(left) - getSystemTemplateSortOrder(right));

    const userTemplates = filteredTemplates
        .filter((template) => !template.isSystem)
        .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());

    return [...systemTemplates, ...userTemplates];
}

// Проверяет, что шаблон — «Рейтинги каталогов» (base4), для которого не нужны отзывы в фильтре.
export function isCatalogRatingsTemplate(template: ExportTemplateType): boolean {
    return getSystemTemplateCode(template) === SystemExportTemplateCodeEnum.Base4;
}

// Формирует тело запроса экспорта через API конструктора из формы списка шаблонов.
export function buildExportConstructorExportPayload(
    formValues: ExportConstructorListFormType,
    selectedTemplate: ExportTemplateType
): ExportConstructorExportPayloadType {
    const {name, additionalUserEmails, commentsLayout, withReviewsSummary, withTagsStat} = formValues;
    const normalizedEmails = normalizeEmails(additionalUserEmails?.trim() ?? '');
    const systemCode = getSystemTemplateCode(selectedTemplate);

    const exportPayload: ExportConstructorExportPayloadType = {
        customReportName: name,
        ...(normalizedEmails.trim() ? {additionalUserEmails: normalizedEmails} : {}),
    };

    if (selectedTemplate.isSystem && systemCode) {
        if (systemCode === SystemExportTemplateCodeEnum.Base) {
            return {
                ...exportPayload,
                systemTemplateCode: systemCode,
                commentsLayout,
                withReviewsSummary,
                withTagsStat,
            };
        }

        return {...exportPayload, systemTemplateCode: systemCode};
    }

    return {...exportPayload, templateId: selectedTemplate.id};
}

// Возвращает колонки каталога, относящиеся к одному типу листа (sheetType).
export function getColumnsForSheetType(
    catalog: Array<ExportColumnCatalogItemType>,
    sheetType: string
): Array<ExportColumnCatalogItemType> {
    return catalog.filter((column) => column.sheetType === sheetType);
}

// Список уникальных типов листов в каталоге колонок.
export function getUniqueSheetTypes(catalog: Array<ExportColumnCatalogItemType>): Array<string> {
    return [...new Set(catalog.map((column) => column.sheetType))];
}

// Человекочитаемое название листа по sheetType из каталога.
export function getSheetLabel(catalog: Array<ExportColumnCatalogItemType>, sheetType: string): string {
    return catalog.find((column) => column.sheetType === sheetType)?.sheetLabel ?? sheetType;
}

// Определяет sheetType пользовательского шаблона по его колонкам и каталогу.
export function inferSheetTypeFromColumns(
    columns: Array<ExportTemplateColumnType>,
    catalog: Array<ExportColumnCatalogItemType>
): string | undefined {
    const columnIds = new Set(columns.map((column) => column.id));
    const matchedColumn = catalog.find((column) => columnIds.has(column.id));

    return matchedColumn?.sheetType;
}

// Собирает массив колонок шаблона из выбранных id и опций формата.
export function mergeSelectedColumns(
    selectedColumnIds: Array<string>,
    columnOptions: Record<string, ExportColumnFormatType | undefined>
): Array<ExportTemplateColumnType> {
    return selectedColumnIds.map((columnId) => {
        const format = columnOptions[columnId];

        if (format) {
            return {id: columnId, options: {format}};
        }

        return {id: columnId};
    });
}

// Извлекает id колонок из шаблона.
export function getSelectedColumnIds(columns: Array<ExportTemplateColumnType>): Array<string> {
    return columns.map((column) => column.id);
}

const exportConstructorPivotColumnIds = ['reviews_summary_pivot', 'tags_pivot'] as const;

// Синтетическая колонка «Адрес» — склейка street+street_number на бэке.
// Скрыта из чекбоксов пикера, управляется радио «Отображение столбцов Улица/Дом».
export const addressColumnId = 'address';
const addressPartColumnIds = ['street', 'street_number'] as const;

// Колонка «Автор» и её разнесённые подполя (имя/телефон/почта, RD-16).
// Подполя скрыты из пикера, управляются радио «Отображение данных автора».
const authorColumnId = 'author';
const authorPartColumnIds = ['author_name', 'author_phone', 'author_email'] as const;

// Проверяет, что колонка — pivot-поле дополнительных данных, а не обычное поле каталога.
export function isExportConstructorPivotColumnId(columnId: string): boolean {
    return (exportConstructorPivotColumnIds as ReadonlyArray<string>).includes(columnId);
}

// Колонки, скрытые из чекбоксов пикера (pivot-поля, склеенный «Адрес», подполя автора).
export function isExportConstructorHiddenPickerColumnId(columnId: string): boolean {
    return (
        isExportConstructorPivotColumnId(columnId) ||
        columnId === addressColumnId ||
        (authorPartColumnIds as ReadonlyArray<string>).includes(columnId)
    );
}

// Применяет режим отображения адреса к списку id колонок:
// merged → street/street_number заменяются одной колонкой address (на позиции первой из них).
export function applyAddressDisplay(selectedColumnIds: Array<string>, addressFormat: AddressFormatType): Array<string> {
    if (addressFormat !== 'merged') {
        return selectedColumnIds;
    }

    const hasAddressPart = selectedColumnIds.some((columnId) =>
        (addressPartColumnIds as ReadonlyArray<string>).includes(columnId)
    );

    if (!hasAddressPart) {
        return selectedColumnIds;
    }

    const result: Array<string> = [];
    let addressInserted = false;

    selectedColumnIds.forEach((columnId) => {
        if ((addressPartColumnIds as ReadonlyArray<string>).includes(columnId)) {
            if (!addressInserted) {
                result.push(addressColumnId);
                addressInserted = true;
            }

            return;
        }

        result.push(columnId);
    });

    return result;
}

// Обратное преобразование порядка из превью (где адрес — одна колонка) в id чекбоксов.
export function expandAddressDisplay(previewColumnIds: Array<string>, selectedColumnIds: Array<string>): Array<string> {
    return previewColumnIds.flatMap((columnId) => {
        if (columnId !== addressColumnId) {
            return [columnId];
        }

        return (addressPartColumnIds as ReadonlyArray<string>).filter((partId) => selectedColumnIds.includes(partId));
    });
}

// Применяет режим отображения автора: separate → колонка author заменяется
// подполями author_name/author_phone/author_email (на позиции author).
export function applyAuthorDisplay(selectedColumnIds: Array<string>, authorFormat: AuthorFormatType): Array<string> {
    if (authorFormat !== 'separate' || !selectedColumnIds.includes(authorColumnId)) {
        return selectedColumnIds;
    }

    return selectedColumnIds.flatMap((columnId) =>
        columnId === authorColumnId ? [...authorPartColumnIds] : [columnId]
    );
}

// Обратное преобразование порядка из превью (где автор — 3 колонки) в id чекбоксов.
export function expandAuthorDisplay(previewColumnIds: Array<string>, selectedColumnIds: Array<string>): Array<string> {
    const shouldCollapse = selectedColumnIds.includes(authorColumnId);
    const result: Array<string> = [];
    let authorInserted = false;

    previewColumnIds.forEach((columnId) => {
        if ((authorPartColumnIds as ReadonlyArray<string>).includes(columnId)) {
            if (shouldCollapse && !authorInserted) {
                result.push(authorColumnId);
                authorInserted = true;
            }

            return;
        }

        result.push(columnId);
    });

    return result;
}

// Возвращает элементы каталога только для колонок шаблона, в порядке из export_templates.
export function getCatalogColumnsForTemplate(
    catalog: Array<ExportColumnCatalogItemType>,
    columns: Array<ExportTemplateColumnType>
): Array<ExportColumnCatalogItemType> {
    const catalogById = new Map(catalog.map((column) => [column.id, column]));

    return columns
        .map((column) => column.id)
        .filter((columnId) => !isExportConstructorPivotColumnId(columnId))
        .map((columnId) => catalogById.get(columnId))
        .filter(Boolean) as Array<ExportColumnCatalogItemType>;
}

// Возвращает все колонки каталога для листа родительского (системного) шаблона.
// Полный набор поля листа (а не только колонки base) — чтобы в кастомной выгрузке
// были доступны новые поля, которых нет в стандартном шаблоне. Скрытые из пикера
// колонки (pivot-поля, склеенный «Адрес») исключаются.
export function getEditorPickerColumns(
    catalog: Array<ExportColumnCatalogItemType>,
    parentTemplate: ExportTemplateType
): Array<ExportColumnCatalogItemType> {
    const sheetType = inferSheetTypeFromColumns(parentTemplate.columns, catalog);

    if (!sheetType) {
        return getCatalogColumnsForTemplate(catalog, parentTemplate.columns);
    }

    return getColumnsForSheetType(catalog, sheetType).filter(
        (column) => !isExportConstructorHiddenPickerColumnId(column.id)
    );
}

// Находит системный шаблон-родитель по id в списке шаблонов.
export function findParentTemplate(
    parentId: number | null | undefined,
    allTemplates: Array<ExportTemplateType>
): ExportTemplateType | null {
    if (!parentId) {
        return null;
    }

    return allTemplates.find((template) => template.id === parentId && template.isSystem) ?? null;
}

// Возвращает id системного шаблона-родителя для создания кастомной выгрузки.
export function resolveParentTemplateId(sourceTemplate: ExportTemplateType): number | null {
    if (sourceTemplate.isSystem) {
        return sourceTemplate.id > 0 ? sourceTemplate.id : null;
    }

    return sourceTemplate.parent;
}

// Начальные значения формы редактора при создании, клонировании или редактировании шаблона.
export function getEditorFormInitialValues(options: {
    isEditMode: boolean;
    availableColumnIds: Array<string>;
    columnSourceTemplate?: ExportTemplateType;
}): Partial<ExportConstructorEditorFormType> {
    const {isEditMode, availableColumnIds, columnSourceTemplate} = options;
    const initialColumns = columnSourceTemplate?.columns ?? [];
    const hasMergedAddress = initialColumns.some((column) => column.id === addressColumnId);
    const hasSeparateAuthor = initialColumns.some((column) =>
        (authorPartColumnIds as ReadonlyArray<string>).includes(column.id)
    );

    // Stored synthetic columns are represented back as their checkbox equivalents,
    // the display mode itself lives in the radio state:
    // merged «Адрес» → Улица/Дом checkboxes; separate author sub-columns → «Автор».
    const expandedColumnIds = getSelectedColumnIds(initialColumns).flatMap((columnId) => {
        if (columnId === addressColumnId) {
            return [...addressPartColumnIds];
        }

        if ((authorPartColumnIds as ReadonlyArray<string>).includes(columnId)) {
            return [authorColumnId];
        }

        return [columnId];
    });
    const selectableColumnIds = [...new Set(expandedColumnIds)].filter(
        (columnId) => availableColumnIds.includes(columnId) && !isExportConstructorHiddenPickerColumnId(columnId)
    );

    return {
        [ExportConstructorEditorFormEnum.Name]: isEditMode ? columnSourceTemplate?.name ?? '' : '',
        [ExportConstructorEditorFormEnum.SelectedColumnIds]: selectableColumnIds,
        [ExportConstructorEditorFormEnum.TagsFormat]:
            initialColumns.find((column) => column.id === 'tags')?.options?.format ?? 'comma',
        [ExportConstructorEditorFormEnum.AutotagsFormat]:
            initialColumns.find((column) => column.id === 'autotags')?.options?.format ?? 'comma',
        [ExportConstructorEditorFormEnum.AddressFormat]: hasMergedAddress ? 'merged' : 'separate',
        [ExportConstructorEditorFormEnum.AuthorFormat]: hasSeparateAuthor ? 'separate' : 'single',
        [ExportConstructorEditorFormEnum.WithReviewsSummary]: initialColumns.some(
            (column) => column.id === 'reviews_summary_pivot'
        ),
        [ExportConstructorEditorFormEnum.WithTagsStat]: initialColumns.some((column) => column.id === 'tags_pivot'),
    };
}

const systemTemplateSheetTypeMap: Record<SystemExportTemplateCodeEnum, string> = {
    [SystemExportTemplateCodeEnum.Base]: 'review_row',
    [SystemExportTemplateCodeEnum.Base1]: 'brand_city',
    [SystemExportTemplateCodeEnum.Base2]: 'brand_city',
    [SystemExportTemplateCodeEnum.Base3]: 'catalog_brand_company',
    [SystemExportTemplateCodeEnum.Base4]: 'company_catalog',
};

// Тип листа в каталоге колонок для системного шаблона по его коду.
export function getSystemTemplateSheetType(code: SystemExportTemplateCodeEnum): string {
    return systemTemplateSheetTypeMap[code];
}

export type ExportConstructorEmailValidationErrorType = {
    errorKey: LangKeyType;
    valueMap?: Record<string, string>;
};

// Валидирует список email через запятую; возвращает ошибку для формы или null.
export function validateExportConstructorEmails(emailValue: string): ExportConstructorEmailValidationErrorType | null {
    const trimmedValue = emailValue.trim();

    if (!trimmedValue) {
        return null;
    }

    const emails = splitCommaSeparatedString(trimmedValue).map((email) => email.trim());

    if (emails.length > 100) {
        return {
            errorKey: 'VALIDATION__ERROR__FIELD_COMMA_SEPARATED_LENGTH',
            valueMap: {length: '100'},
        };
    }

    const invalidEmails = emails.filter((email) => !EMAIL_REGEXP.test(email));

    if (invalidEmails.length > 0) {
        return {
            errorKey: 'VALIDATION__ERROR__FIELD_COMMA_SEPARATED_EMAILS_INCORRECT',
            valueMap: {emails: invalidEmails.join(', ')},
        };
    }

    return null;
}

// Собирает колонки шаблона из значений формы редактора перед сохранением.
export function buildEditorTemplateColumns(formValues: {
    selectedColumnIds: Array<string>;
    tagsFormat: ExportColumnFormatType;
    autotagsFormat: ExportColumnFormatType;
    addressFormat: AddressFormatType;
    authorFormat: AuthorFormatType;
    withReviewsSummary: boolean;
    withTagsStat: boolean;
}): Array<ExportTemplateColumnType> {
    const {
        selectedColumnIds,
        tagsFormat,
        autotagsFormat,
        addressFormat,
        authorFormat,
        withReviewsSummary,
        withTagsStat,
    } = formValues;

    // separate author → author разворачивается в подполя; merged адрес → street/дом в одну колонку.
    const displayColumnIds = applyAddressDisplay(applyAuthorDisplay(selectedColumnIds, authorFormat), addressFormat);

    const columnOptions: Record<string, ExportColumnFormatType | undefined> = {
        ...(displayColumnIds.includes('tags') ? {tags: tagsFormat} : {}),
        ...(displayColumnIds.includes('autotags') ? {autotags: autotagsFormat} : {}),
    };

    const columns = mergeSelectedColumns(displayColumnIds, columnOptions);

    return [
        ...columns,
        ...(withReviewsSummary ? [{id: 'reviews_summary_pivot'}] : []),
        ...(withTagsStat ? [{id: 'tags_pivot'}] : []),
    ];
}

const localSystemExportTemplateTimestamp = '1970-01-01T00:00:00.000Z';
const localLegacyFullExportOldTemplateId = -6;

const localSystemExportTemplateIdMap: Record<SystemExportTemplateCodeEnum, number> = {
    [SystemExportTemplateCodeEnum.Base]: -1,
    [SystemExportTemplateCodeEnum.Base1]: -2,
    [SystemExportTemplateCodeEnum.Base2]: -3,
    [SystemExportTemplateCodeEnum.Base3]: -4,
    [SystemExportTemplateCodeEnum.Base4]: -5,
};

const systemCodeToLegacyMode: Record<SystemExportTemplateCodeEnum, ExportReportModeEnum> = {
    [SystemExportTemplateCodeEnum.Base]: ExportReportModeEnum.Base,
    [SystemExportTemplateCodeEnum.Base1]: ExportReportModeEnum.Base1,
    [SystemExportTemplateCodeEnum.Base2]: ExportReportModeEnum.Base2,
    [SystemExportTemplateCodeEnum.Base3]: ExportReportModeEnum.Base3,
    [SystemExportTemplateCodeEnum.Base4]: ExportReportModeEnum.Base4,
};

// Проверяет, что шаблон из локального fallback-списка (отрицательный id).
export function isLocalSystemExportTemplate(template: ExportTemplateType): boolean {
    return template.id < 0;
}

function createLocalSystemExportTemplate(
    code: SystemExportTemplateCodeEnum,
    columns: Array<ExportTemplateColumnType> = []
): ExportTemplateType {
    return {
        id: localSystemExportTemplateIdMap[code],
        name: '',
        code,
        isSystem: true,
        parent: null,
        columns,
        createdAt: localSystemExportTemplateTimestamp,
        updatedAt: localSystemExportTemplateTimestamp,
    };
}

function createLegacyFullExportOldTemplate(): ExportTemplateType {
    return {
        id: localLegacyFullExportOldTemplateId,
        name: '',
        code: legacyFullExportOldTemplateCode,
        isSystem: true,
        parent: null,
        columns: [],
        createdAt: localSystemExportTemplateTimestamp,
        updatedAt: localSystemExportTemplateTimestamp,
    };
}

// Добавляет «Полную выгрузку (старая версия)» в список, если её нет в ответе API.
export function appendLegacyFullExportOldTemplate(templates: Array<ExportTemplateType>): Array<ExportTemplateType> {
    if (templates.some((template) => isLegacyFullExportOldTemplate(template))) {
        return templates;
    }

    return [...templates, createLegacyFullExportOldTemplate()];
}

// Локальные системные шаблоны, когда /export_templates/ недоступен или пустой.
export function getLocalSystemExportTemplates(domainName: string): Array<ExportTemplateType> {
    const isBase4Excluded = domainName === DomainNameEnum.brandWizard;

    return [
        createLocalSystemExportTemplate(SystemExportTemplateCodeEnum.Base),
        createLegacyFullExportOldTemplate(),
        createLocalSystemExportTemplate(SystemExportTemplateCodeEnum.Base1),
        createLocalSystemExportTemplate(SystemExportTemplateCodeEnum.Base2),
        createLocalSystemExportTemplate(SystemExportTemplateCodeEnum.Base3),
        ...(isBase4Excluded ? [] : [createLocalSystemExportTemplate(SystemExportTemplateCodeEnum.Base4)]),
    ];
}

// Преобразует выбор системного шаблона в параметры legacy-ручки GET /v4/reviews/export/.
export function buildLegacyExportReportOptions(
    formValues: ExportConstructorListFormType,
    template: ExportTemplateType
): ExportReportOptionsType | null {
    if (!template.isSystem) {
        return null;
    }

    const {name, additionalUserEmails, commentsLayout, withReviewsSummary, withTagsStat} = formValues;
    const normalizedEmails = normalizeEmails(additionalUserEmails?.trim() ?? '');
    const emailOptions = normalizedEmails.trim() ? {additionalUserEmails: normalizedEmails} : {};

    if (isLegacyFullExportOldTemplate(template)) {
        return {
            mode: ExportReportModeEnum.Base,
            version: 1,
            customReportName: name,
            ...emailOptions,
        };
    }

    const systemCode = getSystemTemplateCode(template);

    if (!systemCode) {
        return null;
    }

    const mode = systemCodeToLegacyMode[systemCode];

    const baseOptions: ExportReportOptionsType = {
        mode,
        customReportName: name,
        ...emailOptions,
    };

    if (systemCode === SystemExportTemplateCodeEnum.Base) {
        return {
            ...baseOptions,
            version: 2,
            commentsLayout,
            withReviewsSummary,
            withTagsStat,
        };
    }

    return baseOptions;
}
